import React, { useMemo, useState } from 'react';
import { useStores, put, remove, generateId, invalidateStore } from '../hooks/useDB';
import { Paperclip, Plus, X, Receipt, FileText, CheckCircle2, Wallet, ShieldAlert, FileMinus, Trash2, Lock, Loader2, RefreshCw } from 'lucide-react';
import PageLoader from '../components/PageLoader';
import AttachmentLinks from '../components/AttachmentLinks';
import { toast } from '../utils/toast';
import { confirm } from '../utils/confirm';

const CATEGORIES = ['Transport', 'Accommodation', 'Meals', 'Event Supplies', 'Misc'];
const statusClass = {
  pending: 'sh-badge-warning',
  approved: 'sh-badge-info',
  paid: 'sh-badge-success',
  disputed: 'sh-badge-danger',
  cancelled: 'sh-badge-outline'
};

// Attachments are embedded in the row as base64 data URLs, so the whole claim
// travels as one JSON POST. Two separate limits therefore apply:
//   • per file  — keeps any single scan sane
//   • per claim — the backend caps the JSON body at 20MB (api/server.js), and
//                 base64 inflates a file by about a third. Three 5MB files
//                 already reach 20MB encoded, so without a running total a
//                 multi-file claim silently 413s on submit.
// The claim total is measured on the ENCODED length, which is what actually
// goes over the wire, and left under the server cap to leave room for the rest
// of the row.
const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const MAX_CLAIM_ENCODED_BYTES = 18 * 1024 * 1024;

const ACCEPTED = (file) => file.type.startsWith('image/') || file.type === 'application/pdf';

const encodedBytes = (list) => (list || []).reduce((sum, file) => sum + (file.dataUrl ? file.dataUrl.length : 0), 0);

const prettyMB = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)}MB`;

function readFiles(files) {
  const valid = Array.from(files || []).filter(ACCEPTED);
  return Promise.all(valid.map((file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve({
      id: generateId(),
      name: file.name,
      type: file.type,
      size: file.size,
      dataUrl: reader.result,
      uploadedAt: new Date().toISOString()
    });
    // A file that cannot be read resolves to null and is filtered out below,
    // rather than leaving the whole Promise.all pending forever.
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  }))).then((list) => list.filter(Boolean));
}

function Modal({ currentUser, attendee, onSave, onClose }) {
  const [form, setForm] = useState({
    title: '',
    category: 'Transport',
    amount: '',
    vendor: '',
    date: new Date().toISOString().slice(0, 10),
    notes: '',
    attachments: []
  });
  const [saving, setSaving] = useState(false);
  const [dragging, setDragging] = useState(false);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const addAttachment = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;

    const wrongType = list.filter((file) => !ACCEPTED(file));
    if (wrongType.length) {
      toast.error(`Only PDFs and images are accepted. Skipped: ${wrongType.map((f) => f.name).join(', ')}`);
    }
    const oversized = list.filter((file) => ACCEPTED(file) && file.size > MAX_FILE_SIZE_BYTES);
    if (oversized.length) {
      toast.error(`Files must be under ${MAX_FILE_SIZE_MB}MB. Rejected: ${oversized.map((f) => f.name).join(', ')}`);
    }

    const encoded = await readFiles(list.filter((file) => ACCEPTED(file) && file.size <= MAX_FILE_SIZE_BYTES));
    if (!encoded.length) return;

    // Admit files one at a time so a batch that overflows the claim keeps the
    // ones that fit, instead of dropping the whole selection.
    setForm((prev) => {
      let used = encodedBytes(prev.attachments);
      const accepted = [];
      const rejected = [];
      for (const file of encoded) {
        if (used + file.dataUrl.length > MAX_CLAIM_ENCODED_BYTES) rejected.push(file.name);
        else { accepted.push(file); used += file.dataUrl.length; }
      }
      if (rejected.length) {
        toast.error(
          `This claim is at its ${prettyMB(MAX_CLAIM_ENCODED_BYTES)} upload limit. ` +
          `Not added: ${rejected.join(', ')}. Submit these on a second claim.`
        );
      }
      return accepted.length ? { ...prev, attachments: [...prev.attachments, ...accepted] } : prev;
    });
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    addAttachment(e.dataTransfer?.files);
  };

  const removeAttachment = (id) => {
    setForm((prev) => ({ ...prev, attachments: prev.attachments.filter((file) => file.id !== id) }));
  };

  const save = async () => {
    if (!form.title || !form.amount || !form.attachments.length || saving) return;
    setSaving(true);
    try {
      await onSave({
        ...form,
        type: 'expense',
        estimated: form.amount,
        amount: form.amount,
        status: 'pending',
        paymentMethod: 'reimbursement',
        reimbursement: true,
        submittedByEmail: currentUser.email,
        submittedByName: currentUser.name,
        assignedAttendeeIds: attendee?.id ? [attendee.id] : [],
        submittedAt: new Date().toISOString()
      });
    } catch (e) {
      // Without this the rejection escaped as an unhandled promise: the spinner
      // stopped, the drawer stayed open and the employee was told nothing —
      // which is exactly what an oversized multi-file claim used to do.
      toast.error(e.message || 'Could not submit the invoice. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tw-drawer-backdrop">
      <div className="tw-drawer-panel">
        {/* Header */}
        <div className="p-6 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Upload Expense Invoice</h3>
            <p className="text-xs text-slate-500 mt-1">Files will automatically attach to event budget for auditor approval</p>
          </div>
          <button className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Expense Title *</label>
            <input 
              value={form.title} 
              onChange={(e) => set('title', e.target.value)} 
              placeholder="e.g. Airport cab receipt to Hilton" 
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white focus:border-slate-800 outline-none transition"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Category</label>
              <select 
                value={form.category} 
                onChange={(e) => set('category', e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
              >
                {CATEGORIES.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Amount (INR) *</label>
              <input 
                type="number" 
                value={form.amount} 
                onChange={(e) => set('amount', e.target.value)} 
                placeholder="2500" 
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white font-semibold outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Vendor / Payee</label>
              <input 
                value={form.vendor} 
                onChange={(e) => set('vendor', e.target.value)} 
                placeholder="e.g. Uber India" 
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Expense Date</label>
              <input 
                type="date" 
                value={form.date} 
                onChange={(e) => set('date', e.target.value)} 
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none"
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider block">
                Receipts / Invoices *
              </label>
              {form.attachments.length > 0 && (
                <span className="text-[10px] font-semibold text-slate-400">
                  {form.attachments.length} attached · {prettyMB(encodedBytes(form.attachments))} of {prettyMB(MAX_CLAIM_ENCODED_BYTES)}
                </span>
              )}
            </div>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              className={`border-2 border-dashed p-4 rounded-xl text-center transition cursor-pointer relative ${dragging ? 'border-slate-800 bg-slate-100' : 'border-slate-200 hover:border-slate-400 bg-slate-50/50'}`}
            >
              <input
                type="file"
                accept="application/pdf,image/*"
                multiple
                onChange={(e) => { addAttachment(e.target.files); e.target.value = ''; }}
                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
              />
              <Paperclip className="mx-auto text-slate-400 mb-2" size={24} />
              <p className="text-xs font-semibold text-slate-700">
                {dragging ? 'Drop the files here' : 'Click or drag files here — you can pick several at once'}
              </p>
              <p className="text-[10px] text-slate-400 mt-1">
                PDF or image, up to {MAX_FILE_SIZE_MB}MB each and {prettyMB(MAX_CLAIM_ENCODED_BYTES)} per claim
              </p>
            </div>

            {form.attachments.map((file) => (
              <div key={file.id} className="flex items-center justify-between border border-emerald-100 bg-emerald-50/20 p-2.5 rounded-lg animate-fadeIn text-xs">
                <div className="flex items-center gap-2 text-slate-800 font-medium truncate">
                  <FileText className="text-emerald-500 shrink-0" size={14} />
                  <span className="truncate">{file.name}</span>
                  <span className="text-[10px] text-slate-400 font-normal">({Math.round((file.size || 0) / 1024)} KB)</span>
                </div>
                <button 
                  type="button" 
                  className="text-xs text-red-500 hover:text-red-700 font-bold px-1 py-0.5 rounded" 
                  onClick={() => removeAttachment(file.id)}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Business Purpose Notes</label>
            <textarea 
              value={form.notes} 
              onChange={(e) => set('notes', e.target.value)} 
              rows={2} 
              placeholder="Provide exact justification, client codes, or payout remarks..." 
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:bg-white outline-none resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-100 flex gap-3 justify-end bg-white">
          <button className="px-4 py-2 text-sm font-semibold border border-slate-200 hover:bg-slate-50 rounded-lg transition" onClick={onClose}>
            Cancel
          </button>
          <button
            className="px-4 py-2 text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            onClick={save}
            disabled={!form.title || !form.amount || !form.attachments.length || saving}
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            {saving ? 'Submitting…' : 'Submit for Review'}
          </button>
        </div>
      </div>
    </div>
  );
}

const EI_STORES = ['budget', 'attendees'];

// Finance-controlled statuses can't be deleted by the submitter — only the
// auditor can move them once they're in the approval/payout pipeline.
const LOCKED_STATUSES = ['approved', 'paid'];

export default function EmployeeInvoices({ currentUser = {} }) {
  const { budget, attendees, loading } = useStores(EI_STORES);
  const [modalOpen, setModalOpen] = useState(false);

  const attendee = useMemo(
    () => attendees.find((row) => (row.email || '').toLowerCase() === (currentUser.email || '').toLowerCase()) || null,
    [attendees, currentUser.email]
  );

  const entries = useMemo(
    () => budget.filter((row) => row.reimbursement && (row.submittedByEmail || '').toLowerCase() === (currentUser.email || '').toLowerCase()),
    [budget, currentUser.email]
  );

  const save = async (form) => {
    await put('budget', { ...form, id: generateId() });
    setModalOpen(false);
    window.dispatchEvent(new CustomEvent('planner:data-change'));
  };

  const del = async (row) => {
    if (LOCKED_STATUSES.includes(row.status)) {
      toast.error('Approved or paid invoices are locked — contact finance to reverse.');
      return;
    }
    const ok = await confirm(
      `Remove "${row.title}"? This permanently deletes the invoice from the budget ledger.`,
      'Delete Invoice'
    );
    if (!ok) return;
    try {
      await remove('budget', row.id);
      window.dispatchEvent(new CustomEvent('planner:data-change'));
      toast.success('Invoice deleted.');
    } catch {
      toast.error('Failed to delete invoice.');
    }
  };

  const totalSubmitted = entries.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);
  const totalPaid = entries.filter((row) => row.status === 'paid').reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);
  const pending = entries.filter((row) => !['paid', 'cancelled'].includes(row.status)).length;

  if (loading) return <PageLoader label="Loading your invoices…" sublabel="Fetching your reimbursement ledger" />;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold font-display text-slate-900 flex items-center gap-2">
            <Receipt style={{ color: 'var(--accent)' }} size={24} />
            <span>My Expense Invoices</span>
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Submit booking receipts, dinner bills, and supplies for immediate corporate approval and dispatch
          </p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <button
            className="px-3 py-2 border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-lg text-sm font-semibold transition flex items-center gap-2 shadow-sm"
            onClick={() => invalidateStore('budget')}
            disabled={loading}
            title="Refresh to see the latest invoice status"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </button>
          <button
            className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-semibold transition flex items-center gap-2 shadow-sm"
            onClick={() => setModalOpen(true)}
          >
            <Plus size={16} />
            <span>Upload Invoice</span>
          </button>
        </div>
      </div>

      {/* Summary Matrix Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="sh-stat-card border-l-4 border-slate-900">
          <div className="sh-stat-head">
            <span className="sh-stat-label">Total Expense Submitted</span>
            <Wallet className="text-slate-500" size={14} />
          </div>
          <div className="sh-stat-value text-slate-900 font-display">₹{totalSubmitted.toLocaleString('en-IN')}</div>
          <span className="text-xs text-slate-500 font-medium block mt-0.5">{entries.length} requests in ledger</span>
        </div>

        <div className="sh-stat-card border-l-4 border-amber-500">
          <div className="sh-stat-head">
            <span className="sh-stat-label">Active / Under Review</span>
            <ShieldAlert className="text-amber-500" size={14} />
          </div>
          <div className="sh-stat-value text-slate-900 font-display">{pending} items</div>
          <span className="text-xs text-slate-500 block">Pending auditing validation</span>
        </div>

        <div className="sh-stat-card border-l-4 border-emerald-500">
          <div className="sh-stat-head">
            <span className="sh-stat-label">Fully Reimbursed</span>
            <CheckCircle2 className="text-emerald-500" size={14} />
          </div>
          <div className="sh-stat-value text-emerald-600 font-display">₹{totalPaid.toLocaleString('en-IN')}</div>
          <span className="text-xs text-slate-500 block">Paid out directly to your banking ledger</span>
        </div>
      </div>

      {/* Roster Matching Advisory */}
      {!attendee && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 text-sm text-amber-900 leading-normal">
          <ShieldAlert className="text-amber-500 shrink-0 mt-0.5" size={16} />
          <div>
            <span className="font-semibold block">Roster Match Warning</span>
            Your interactive sign-in email (<span className="font-mono text-xs font-semibold">{currentUser.email || 'guest'}</span>) is not linked to an official planner registry. Expenses will still resolve appropriately, but without matching profile variables.
          </div>
        </div>
      )}

      {/* Invoices List table view */}
      {entries.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center shadow-sm">
          <FileMinus className="mx-auto text-slate-300 mb-3" size={36} />
          <h3 className="text-base font-bold text-slate-800 font-display">No invoices submitted yet</h3>
          <p className="text-sm text-slate-500 mt-1">Scan or drag-drop transport vouchers, cab bills, or decor fees directly</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden text-sm">
          <div className="table-wrap">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-200">
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-left">Expense Detail</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-left">Category</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-left">Amount Requested</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-left w-24">Status</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-left">Invoice Attachment</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-left">Filing Date</th>
                  <th className="py-3 px-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right w-20">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {entries.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50/20 transition">
                    <td className="py-3 px-4">
                      <div className="font-semibold text-slate-900">{row.title}</div>
                      {row.vendor && <div className="text-xs text-slate-500">Payee: {row.vendor}</div>}
                    </td>
                    <td className="py-3 px-4 text-slate-500 font-medium text-xs font-display">
                      {row.category}
                    </td>
                    <td className="py-3 px-4 font-mono font-bold text-slate-900 text-sm">
                      ₹{Number(row.amount || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`sh-badge ${statusClass[row.status] || 'sh-badge-outline'}`}>{row.status}</span>
                    </td>
                    <td className="py-3 px-4">
                      <AttachmentLinks attachments={row.attachments} />
                    </td>
                    <td className="py-3 px-4 text-xs text-slate-500 font-mono">
                      {row.date || '—'}
                    </td>
                    <td className="py-3 px-4 text-right">
                      {LOCKED_STATUSES.includes(row.status) ? (
                        <span
                          className="inline-flex items-center gap-1 text-slate-400 text-xs font-medium"
                          title="Approved or paid invoices are locked by finance"
                        >
                          <Lock size={13} className="shrink-0" />
                          <span className="hidden sm:inline">Locked</span>
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => del(row)}
                          title="Delete invoice"
                          className="inline-flex items-center gap-1 text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded-lg text-xs font-semibold transition"
                        >
                          <Trash2 size={14} className="shrink-0" />
                          <span className="hidden sm:inline">Delete</span>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modalOpen && (
        <Modal 
          currentUser={currentUser} 
          attendee={attendee} 
          onSave={save} 
          onClose={() => setModalOpen(false)} 
        />
      )}
    </div>
  );
}
